import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * Action button configuration for EmptyState.
 */
export interface EmptyStateAction {
  /** Button label text. */
  label: string;
  /** Click handler. */
  onClick: () => void;
}

/**
 * Props for the EmptyState component.
 */
export interface EmptyStateProps {
  /** Icon element displayed above the title. */
  icon?: ReactNode;
  /** Primary heading text. */
  title: string;
  /** Secondary descriptive text. */
  description?: string;
  /** Optional call-to-action button configuration. */
  action?: EmptyStateAction;
  /** Additional CSS classes for the wrapper. */
  className?: string;
}

/**
 * Placeholder displayed when a list or data set is empty.
 * Optionally includes an icon, description, and a call-to-action button.
 *
 * @param props.icon - Illustrative icon element
 * @param props.title - Main empty-state heading
 * @param props.description - Supporting copy
 * @param props.action - Optional CTA button
 * @example
 * ```tsx
 * <EmptyState
 *   icon={<CalendarIcon className="h-12 w-12" />}
 *   title="No events yet"
 *   description="Create your first event to get started."
 *   action={{ label: 'Create event', onClick: handleCreate }}
 * />
 * ```
 */
export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-dashed border-ink/20 px-6 py-12 text-center',
        className,
      )}
    >
      {icon != null && <div className="mb-4 text-ink-muted">{icon}</div>}
      <h3 className="mb-1 text-base font-semibold text-ink">{title}</h3>
      {description != null && <p className="mb-4 max-w-sm text-sm text-ink-muted">{description}</p>}
      {action != null && (
        <button
          type="button"
          onClick={action.onClick}
          className="rounded-md bg-[var(--color-primary,theme(colors.blue.600))] px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary,theme(colors.blue.500))] focus:ring-offset-2"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
